/**
 * KAN-958: столбцы таблицы CRM со стабильными кодами. Пользователь скрывает
 * столбцы, настройка хранится на сервере списком СКРЫТЫХ кодов
 * (ui_settings.crm_table.hidden_columns): новые столбцы по умолчанию видны,
 * неизвестные коды игнорируются. Обязательные столбцы (Телефон закреплён и задаёт
 * сдвиг Контакта, Контакт, Действия) не скрываются.
 * Столбец атрибута лида: код attr:<id атрибута>.
 * Порядок столбцов задаётся только здесь: CrmTable рисует шапку и ячейки по этому
 * списку. Закреплённые Телефон и Контакт, затем важные (Следующая связь,
 * Комментарий, Причина, Город, Организация, Telegram), затем остальные, Действия справа.
 */
import type { CrmAttribute } from '@/types/crm';

export interface CrmTableColumn {
  code: string;
  label: string;
  /** Обязательный столбец: не скрывается. */
  required: boolean;
}

const col = (code: string, label: string, required = false): CrmTableColumn => ({ code, label, required });

const PINNED_LEFT = [col('phone', 'Телефон', true), col('contact', 'Контакт', true)];
/** KAN-958: важные столбцы сразу за закреплёнными, в этом порядке. */
const PRIMARY = [
  col('next_contact', 'Следующая связь'),
  col('comment', 'Комментарий'),
  col('reason', 'Причина'),
  col('city', 'Город'),
  col('company', 'Организация'),
  col('telegram', 'Telegram'),
];
const DATE_COLUMNS = [col('date', 'Дата')];
/** KAN-880: таблица лидов вместо "Дата". */
const LEAD_COLUMNS = [col('group', 'Группа'), col('days', 'Срок'), col('taken', 'Взят')];
const SECONDARY = [col('stage', 'Этап'), col('manager', 'Менеджер')];
/** KAN-958: "Заказы" вместе с текущим заказом (черновиком) одним столбцом. */
const COLUMNS_AFTER_ATTRIBUTES = [col('orders', 'Заказы')];
const ACTIONS = col('actions', 'Действия', true);

export function attributeColumnCode(attributeId: string): string {
  return `attr:${attributeId}`;
}

interface ColumnsOptions {
  /** KAN-880: таблица лидов (Группа, Срок, Взят вместо Дата). */
  leadMode: boolean;
  attributes: readonly CrmAttribute[];
}

/** Все столбцы таблицы в порядке отрисовки. */
export function crmTableColumns({ leadMode, attributes }: ColumnsOptions): CrmTableColumn[] {
  return [
    ...PINNED_LEFT,
    ...PRIMARY,
    ...(leadMode ? LEAD_COLUMNS : DATE_COLUMNS),
    ...SECONDARY,
    ...attributes.map((attribute) => col(attributeColumnCode(attribute.id), attribute.name)),
    ...COLUMNS_AFTER_ATTRIBUTES,
    ACTIONS,
  ];
}

/** Скрытые столбцы среди текущих: неизвестные коды и обязательные столбцы отброшены. */
export function hiddenColumnSet(columns: readonly CrmTableColumn[], hidden: readonly string[]): Set<string> {
  const wanted = new Set(hidden);
  return new Set(columns.filter((c) => !c.required && wanted.has(c.code)).map((c) => c.code));
}

export function hiddenColumnCount(columns: readonly CrmTableColumn[], hidden: readonly string[]): number {
  return hiddenColumnSet(columns, hidden).size;
}

/** Новый список скрытых: visible=false добавляет код (один раз), true убирает. */
export function withColumnVisible(hidden: readonly string[], code: string, visible: boolean): string[] {
  if (visible) return hidden.filter((c) => c !== code);
  return hidden.includes(code) ? [...hidden] : [...hidden, code];
}

/** "Показать все": убрать коды текущих столбцов, коды других (например, атрибутов) сохранить. */
export function showAllColumns(hidden: readonly string[], columns: readonly CrmTableColumn[]): string[] {
  const current = new Set(columns.map((c) => c.code));
  return hidden.filter((c) => !current.has(c));
}
